import React from 'react';
import { formatCurrency } from '../utils/formatter';

function HeroGauge({ forecastData }) {
  if (!forecastData || !forecastData.base) {
    return null;
  }

  // Find EOY 2026 data point for each scenario
  const targetDate = '2026-12';

  const getRevenueAtDate = (data, date) => {
    const dataPoint = data.find(d => d.month === date);
    return dataPoint ? dataPoint.cumulativeRevenue : 0;
  };

  const baseRevenue = getRevenueAtDate(forecastData.base, targetDate);
  const bestRevenue = getRevenueAtDate(forecastData.best, targetDate);
  const worstRevenue = getRevenueAtDate(forecastData.worst, targetDate);

  // Dynamic target based on base scenario's final cumulative ARR
  const targetValue = baseRevenue;

  const getPercentage = (revenue) => Math.min((revenue / targetValue) * 100, 100);

  const basePercentage = getPercentage(baseRevenue);
  const bestPercentage = getPercentage(bestRevenue);
  const worstPercentage = getPercentage(worstRevenue);

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-xl font-semibold mb-6">Progress Toward {formatCurrency(targetValue)} EOY 2026 Goal</h3>

      <div className="grid grid-cols-3 gap-6 mb-6">
        {/* Worst Case */}
        <div className="text-center">
          <div className="text-sm text-gray-500 mb-2">Worst Case</div>
          <div className="relative w-32 h-32 mx-auto">
            <svg className="transform -rotate-90 w-32 h-32">
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="currentColor"
                strokeWidth="8"
                fill="transparent"
                className="text-gray-200"
              />
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="currentColor"
                strokeWidth="8"
                fill="transparent"
                strokeDasharray={`${2 * Math.PI * 56}`}
                strokeDashoffset={`${2 * Math.PI * 56 * (1 - worstPercentage / 100)}`}
                className="text-red-500 transition-all duration-1000"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-2xl font-bold text-red-600">{worstPercentage.toFixed(0)}%</div>
            </div>
          </div>
          <div className="mt-2 text-sm font-medium">{formatCurrency(worstRevenue)}</div>
          <div className="text-xs text-gray-500">Gap: {formatCurrency(targetValue - worstRevenue)}</div>
        </div>

        {/* Base Case */}
        <div className="text-center">
          <div className="text-sm text-gray-500 mb-2">Base Case</div>
          <div className="relative w-32 h-32 mx-auto">
            <svg className="transform -rotate-90 w-32 h-32">
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="currentColor"
                strokeWidth="8"
                fill="transparent"
                className="text-gray-200"
              />
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="currentColor"
                strokeWidth="8"
                fill="transparent"
                strokeDasharray={`${2 * Math.PI * 56}`}
                strokeDashoffset={`${2 * Math.PI * 56 * (1 - basePercentage / 100)}`}
                className="text-blue-500 transition-all duration-1000"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-2xl font-bold text-blue-600">{basePercentage.toFixed(0)}%</div>
            </div>
          </div>
          <div className="mt-2 text-sm font-medium">{formatCurrency(baseRevenue)}</div>
          <div className="text-xs text-gray-500">
            {baseRevenue >= targetValue ? 'Target achieved!' : `Gap: ${formatCurrency(targetValue - baseRevenue)}`}
          </div>
        </div>

        {/* Best Case */}
        <div className="text-center">
          <div className="text-sm text-gray-500 mb-2">Best Case</div>
          <div className="relative w-32 h-32 mx-auto">
            <svg className="transform -rotate-90 w-32 h-32">
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="currentColor"
                strokeWidth="8"
                fill="transparent"
                className="text-gray-200"
              />
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="currentColor"
                strokeWidth="8"
                fill="transparent"
                strokeDasharray={`${2 * Math.PI * 56}`}
                strokeDashoffset={`${2 * Math.PI * 56 * (1 - bestPercentage / 100)}`}
                className="text-green-500 transition-all duration-1000"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-2xl font-bold text-green-600">{bestPercentage.toFixed(0)}%</div>
            </div>
          </div>
          <div className="mt-2 text-sm font-medium">{formatCurrency(bestRevenue)}</div>
          <div className="text-xs text-gray-500">
            {bestRevenue >= targetValue ? `Exceeds by ${formatCurrency(bestRevenue - targetValue)}` : `Gap: ${formatCurrency(targetValue - bestRevenue)}`}
          </div>
        </div>
      </div>

      <div className="text-center">
        <div className="text-sm text-gray-500">Target: {formatCurrency(targetValue)} by December 2026</div>
        <div className="text-sm text-gray-500 mt-1">Current: {formatCurrency(forecastData.currentRevenue)}</div>
      </div>
    </div>
  );
}

export default HeroGauge;
